import { __decorate, __metadata } from 'tslib';
import { Inject, InjectorService, DynamicLoader as DynamicLoader$1, ProviderRegistry } from '@tencent/ioc';
import { DynamicLoader, IInstantiationHookPoint } from '@tencent/containerized';
import { nameOfClass, Metadata } from '@tencent/workbench-core';
import { ActionPointRunnerBuilder } from '../action-point/action-point-runner-builder.js';
import { getComposedMiddleWares } from '../action-point/compose-middlewares.js';
import { TrackControllerService } from '../../services/controller/track-controller-service.js';
import { updateUIStateWithServiceCollection } from '../../services/update-ui-state-with-service-collection.js';

class ControllerBuilder {
    constructor(provider, serviceCollection, postDepsConstructorCallback) {
        this.provider = provider;
        this.serviceCollection = serviceCollection;
        this.postDepsConstructorCallback = postDepsConstructorCallback;
        this.affectedUIService = new Set();
        this.buildAsyncEffect();
        this.serviceToItems = new Map();
        this.serviceCollection.getControllerRelatedUIServices().forEach((service, key) => {
            var _a;
            const itemName = this.serviceCollection.getItemName(key);
            this.serviceToItems.set(key, (_a = this.provider[itemName]) !== null && _a !== void 0 ? _a : []);
        });
    }
    /**
     * 构建 Controller 中的 ActionPoint，注册 UI Config
     */
    build(updateImmediately = false) {
        this.buildEntries();
        this.buildCallbacks();
        if (updateImmediately) {
            this.updateAffectedUIService();
        }
        return this;
    }
    getAffectedUIServices() {
        return this.affectedUIService;
    }
    buildCallbacks() {
        const ctrl = this.provider;
        ctrl.actionPoints.forEach((actionPointMetaData) => {
            const middlewares = getComposedMiddleWares(this.provider.middlewares, actionPointMetaData);
            const points = actionPointMetaData.get('points');
            points.forEach((point) => {
                this.buildByPoints(point, middlewares, actionPointMetaData);
            });
        });
    }
    buildByPoints(point, middlewares, actionPointMetaData) {
        if (this.serviceCollection.getControllerRelatedUIServices().has(point.from)) {
            const service = InjectorService.get(point.from);
            const runner = new ActionPointRunnerBuilder(actionPointMetaData, point, middlewares)
                .buildActionPointRunner();
            service.bind(point.name, point.trigger, runner);
            this.trackControllerService.setCallbackController(point.from + point.name, this.provider.useClass);
            this.affectedUIService.add(point.from);
        }
    }
    /**
     * 注册config到对应service
     */
    buildEntries() {
        this.serviceCollection.getControllerRelatedUIServices().forEach((service) => {
            const name = nameOfClass(service);
            const items = this.getProviderItemsByName(name);
            if (items) {
                items.forEach((config) => {
                    service.register(Object.assign({}, config), false);
                    this.trackControllerService.setConfigController(name + config.name, this.provider.useClass);
                    // 记录当前controller影响了哪些UI Service
                    this.affectedUIService.add(name);
                });
            }
        });
    }
    /**
     * 异步加载的feature可能影响当前controller
     */
    buildAsyncEffect() {
        this.buildIocAsyncEffect();
        this.buildContainerAsyncEffect();
    }
    /**
     * DynamicLoader在Ioc容器中时
     */
    buildIocAsyncEffect() {
        const paramsTypes = Metadata.getParamTypes(this.provider.provide);
        paramsTypes.forEach((typeItem) => {
            if (!InjectorService.hasClass(typeItem)) {
                return;
            }
            const typeClass = InjectorService.getClass(typeItem);
            // 存在动态依赖的feature
            if (typeClass === DynamicLoader$1) {
                const loader = InjectorService.get(typeItem);
                loader.onModuleLoad = (featureModule) => {
                    var _a;
                    const provider = ProviderRegistry.get(featureModule);
                    const token = (_a = provider === null || provider === void 0 ? void 0 : provider.provide) !== null && _a !== void 0 ? _a : featureModule;
                    // 动态实例创建后需要更新相应的UIService
                    InjectorService.addPostConstructor(token, () => {
                        this.postDepsConstructorCallback(this.provider.provide);
                    });
                };
            }
        });
    }
    /**
     * DynamicLoader在Containerized容器中时
     */
    buildContainerAsyncEffect() {
        const { container } = InjectorService;
        if (!container) {
            return;
        }
        const dependencyKeys = container.getServiceDependency(this.provider.provide);
        dependencyKeys.forEach((dependency) => {
            const typeClass = container.getClass(dependency);
            if (typeClass && Object.getPrototypeOf(typeClass) === DynamicLoader) {
                container.addHook(dependency, IInstantiationHookPoint.POST_CONSTRUCTOR, () => {
                    this.postDepsConstructorCallback(this.provider.provide);
                });
            }
        });
    }
    /**
     * 按需更新UI Service
     */
    updateAffectedUIService() {
        updateUIStateWithServiceCollection(this.serviceCollection, this.affectedUIService);
    }
    /**
     * provider item 于service 关系对照
     */
    getProviderItemsByName(name) {
        return this.serviceToItems.get(name);
    }
}
__decorate([
    Inject(TrackControllerService),
    __metadata("design:type", TrackControllerService)
], ControllerBuilder.prototype, "trackControllerService", void 0);

export { ControllerBuilder };
//# sourceMappingURL=controller-builder.js.map
